import type { DramaSubtitleTrack } from '@/api/member';

export interface SubtitleCue {
  start: number;
  end: number;
  text: string;
}

const subtitleCueCache = new Map<string, Promise<SubtitleCue[]>>();
const subtitleSegmentCache = new Map<string, string[]>();

function baseLanguage(value?: string) {
  return String(value || '').trim().replace('_', '-').split('-')[0].toLowerCase();
}

export function isChineseInterfaceLanguage(language?: string) {
  return baseLanguage(language) === 'zh';
}

export function selectInterfaceSubtitle(
  tracks: DramaSubtitleTrack[],
  interfaceLanguage?: string,
): DramaSubtitleTrack | null {
  const normalized = String(interfaceLanguage || '').trim().replace('_', '-').toLowerCase();
  const sourceTrack = tracks.find((track) => track.kind === 'captions');
  const sourceLanguage = sourceTrack?.languageCode
    || tracks.find((track) => track.sourceLanguage)?.sourceLanguage
    || '';
  if (isChineseInterfaceLanguage(interfaceLanguage) && isChineseInterfaceLanguage(sourceLanguage)) {
    return null;
  }
  const exact = tracks.find((track) => track.languageCode.toLowerCase() === normalized);
  if (exact) return exact;
  const base = baseLanguage(normalized);
  return tracks.find((track) => baseLanguage(track.languageCode) === base) || null;
}

function parseTimestamp(value: string) {
  const parts = value.trim().replace(',', '.').split(':').map(Number);
  if (parts.some((part) => !Number.isFinite(part))) return Number.NaN;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return Number.NaN;
}

export function parseWebVTT(value: string): SubtitleCue[] {
  const blocks = value.replace(/^\uFEFF/, '').replace(/\r/g, '').split(/\n{2,}/);
  const cues: SubtitleCue[] = [];
  for (const block of blocks) {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    const timingIndex = lines.findIndex((line) => line.includes('-->'));
    if (timingIndex < 0) continue;
    const timing = lines[timingIndex].match(/((?:\d{2}:)?\d{2}:\d{2}[.,]\d{3})\s*-->\s*((?:\d{2}:)?\d{2}:\d{2}[.,]\d{3})/);
    if (!timing) continue;
    const start = parseTimestamp(timing[1]);
    const end = parseTimestamp(timing[2]);
    const text = lines.slice(timingIndex + 1).join('\n').replace(/<[^>]+>/g, '').trim();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !text) continue;
    cues.push({ start, end, text });
  }
  return cues;
}

export function loadSubtitleCues(track: DramaSubtitleTrack): Promise<SubtitleCue[]> {
  const cacheKey = `${track.id}:${track.version}:${track.url}`;
  const cached = subtitleCueCache.get(cacheKey);
  if (cached) return cached;
  const request = fetch(track.url, { cache: 'force-cache' })
    .then((response) => response.ok ? response.text() : Promise.reject(new Error(`subtitle HTTP ${response.status}`)))
    .then(parseWebVTT)
    .catch((error) => {
      subtitleCueCache.delete(cacheKey);
      throw error;
    });
  subtitleCueCache.set(cacheKey, request);
  return request;
}

function subtitleCharacterWidth(character: string) {
  return /^[\u0000-\u00ff]$/.test(character) ? 1 : 2;
}

export function splitSubtitleText(text: string, maxWidth: number): string[] {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return [];
  const cacheKey = `${maxWidth}:${normalized}`;
  const cached = subtitleSegmentCache.get(cacheKey);
  if (cached) return cached;

  const result: string[] = [];
  let remaining = Array.from(normalized);
  while (remaining.length > 0) {
    let width = 0;
    let end = 0;
    let preferredEnd = 0;
    for (; end < remaining.length; end += 1) {
      const nextWidth = width + subtitleCharacterWidth(remaining[end]);
      if (nextWidth > maxWidth && end > 0) break;
      width = nextWidth;
      if (/[，。！？、；：,.!?;:]/.test(remaining[end]) && width >= maxWidth * 0.5) {
        preferredEnd = end + 1;
      }
    }
    const take = preferredEnd > 0 ? preferredEnd : Math.max(1, end);
    result.push(remaining.slice(0, take).join('').trim());
    remaining = remaining.slice(take);
    while (remaining[0] === ' ') remaining.shift();
  }
  const segments = result.filter(Boolean);
  subtitleSegmentCache.set(cacheKey, segments);
  return segments;
}

export function subtitleTextAt(cues: SubtitleCue[], currentTime: number, maxWidth = 48) {
  const cue = cues.find((item) => currentTime >= item.start && currentTime < item.end);
  if (!cue) return '';
  const segments = splitSubtitleText(cue.text, maxWidth);
  if (segments.length <= 1) return segments[0] || '';
  const progress = Math.max(0, Math.min(0.999999, (currentTime - cue.start) / (cue.end - cue.start)));
  return segments[Math.floor(progress * segments.length)] || '';
}
